
import { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/hooks/useAuth';

const MAX_FREE_USAGES = 10; // Limite globale
const MAX_FREE_TOOL_USAGES = 3; // Limite par outil
const USAGE_STORAGE_KEY = 'aluna-free-tool-usages';
const TOOL_USAGE_STORAGE_KEY = 'aluna-tool-specific-usages'; // Nouvelle clé pour stocker les utilisations par outil
const VISITED_TOOLS_KEY = 'aluna-visited-tools';
const LAST_ACTION_TIMESTAMP_KEY = 'aluna-last-action-timestamp';

export const useToolUsageLimit = (specificToolName?: string) => {
  const { user } = useAuth();
  const [usagesCount, setUsagesCount] = useState<number>(0);
  const [toolUsagesCount, setToolUsagesCount] = useState<number>(0);
  const [hasReachedLimit, setHasReachedLimit] = useState<boolean>(false);
  const [hasReachedToolLimit, setHasReachedToolLimit] = useState<boolean>(false);
  const [visitedTools, setVisitedTools] = useState<string[]>([]);

  // Charger les données d'utilisation depuis localStorage
  const loadUsageData = () => {
    // Si l'utilisateur est connecté, on ne limite pas ses utilisations
    if (user) {
      setHasReachedLimit(false);
      setHasReachedToolLimit(false);
      return;
    }
    
    // Charger le compteur global du localStorage
    const storedUsages = localStorage.getItem(USAGE_STORAGE_KEY);
    if (storedUsages) {
      const count = parseInt(storedUsages, 10);
      setUsagesCount(count);
      setHasReachedLimit(count >= MAX_FREE_USAGES);
    }
    
    // Charger les outils déjà visités
    const storedVisitedTools = localStorage.getItem(VISITED_TOOLS_KEY);
    if (storedVisitedTools) {
      setVisitedTools(JSON.parse(storedVisitedTools));
    }
    
    // Charger les utilisations spécifiques à l'outil si un nom d'outil est fourni
    if (specificToolName) {
      const storedToolUsages = localStorage.getItem(TOOL_USAGE_STORAGE_KEY);
      if (storedToolUsages) {
        const toolUsages = JSON.parse(storedToolUsages);
        const toolCount = toolUsages[specificToolName] || 0;
        setToolUsagesCount(toolCount);
        setHasReachedToolLimit(toolCount >= MAX_FREE_TOOL_USAGES);
      }
    }
  };

  useEffect(() => {
    loadUsageData();
  }, [user, specificToolName]);

  // Méthode principale pour incrémenter le compteur d'utilisations
  const incrementUsage = (toolName?: string, actionType?: string) => {
    // Si l'utilisateur est connecté, on n'incrémente pas
    if (user) return false;
    
    // Protection contre les appels multiples dans un court laps de temps
    const now = Date.now();
    const lastActionTimestamp = localStorage.getItem(LAST_ACTION_TIMESTAMP_KEY);
    
    if (lastActionTimestamp) {
      const lastActionTime = parseInt(lastActionTimestamp, 10);
      // Empêcher les incrémentations multiples dans un délai de 2 secondes
      if (now - lastActionTime < 2000) {
        return hasReachedLimit || (toolName === specificToolName && hasReachedToolLimit);
      }
    }
    
    // Mettre à jour le timestamp de la dernière action
    localStorage.setItem(LAST_ACTION_TIMESTAMP_KEY, now.toString());
    
    // Si on a spécifié un nom d'outil et qu'il a déjà été visité, on n'incrémente que 
    // si une action spécifique a été effectuée (comme exporter, générer, etc.)
    if (toolName && visitedTools.includes(toolName) && actionType === "page-view") {
      return hasReachedLimit || (toolName === specificToolName && hasReachedToolLimit);
    }
    
    // Incrémenter le compteur global
    const newCount = usagesCount + 1;
    setUsagesCount(newCount);
    localStorage.setItem(USAGE_STORAGE_KEY, newCount.toString());
    
    // Si un nom d'outil est fourni, l'ajouter à la liste des outils visités
    if (toolName && !visitedTools.includes(toolName)) {
      const newVisitedTools = [...visitedTools, toolName];
      setVisitedTools(newVisitedTools);
      localStorage.setItem(VISITED_TOOLS_KEY, JSON.stringify(newVisitedTools));
    }
    
    // Incrémenter le compteur spécifique à l'outil si un nom d'outil est fourni
    if (toolName) {
      const storedToolUsages = localStorage.getItem(TOOL_USAGE_STORAGE_KEY) || '{}';
      const toolUsages = JSON.parse(storedToolUsages);
      const currentToolCount = toolUsages[toolName] || 0;
      const newToolCount = currentToolCount + 1;
      
      toolUsages[toolName] = newToolCount;
      localStorage.setItem(TOOL_USAGE_STORAGE_KEY, JSON.stringify(toolUsages));
      
      // Mettre à jour le compteur spécifique si c'est l'outil actuel
      if (toolName === specificToolName) {
        setToolUsagesCount(newToolCount);
        setHasReachedToolLimit(newToolCount >= MAX_FREE_TOOL_USAGES);
      }
    }
    
    // Définir si la limite globale est atteinte
    const limitReached = newCount >= MAX_FREE_USAGES;
    setHasReachedLimit(limitReached);
    
    // Recharger les données d'utilisation pour mettre à jour l'interface
    loadUsageData();
    
    // Retourner true si la limite globale ou celle de l'outil est atteinte
    return limitReached || (toolName === specificToolName && hasReachedToolLimit);
  };

  const resetUsages = () => {
    setUsagesCount(0);
    setToolUsagesCount(0);
    setVisitedTools([]);
    localStorage.setItem(USAGE_STORAGE_KEY, '0');
    localStorage.setItem(VISITED_TOOLS_KEY, JSON.stringify([]));
    localStorage.setItem(TOOL_USAGE_STORAGE_KEY, JSON.stringify({}));
    setHasReachedLimit(false);
    setHasReachedToolLimit(false);
  };

  const ToolUsageCounter = () => {
    // Ne pas afficher pour les utilisateurs connectés
    if (user) return null;
    
    // Si aucun nom d'outil spécifique n'est fourni, ne pas afficher
    if (!specificToolName) return null;
    
    const usagesLeft = MAX_FREE_TOOL_USAGES - toolUsagesCount;
    const percentageUsed = (toolUsagesCount / MAX_FREE_TOOL_USAGES) * 100;
    
    return (
      <div className="flex flex-col gap-2 w-full max-w-xs mx-auto mb-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Utilisations de cet outil: {toolUsagesCount}/{MAX_FREE_TOOL_USAGES}</span>
          <span>{usagesLeft} restante{usagesLeft > 1 ? 's' : ''}</span>
        </div>
        <Progress value={percentageUsed} className="h-2" />
      </div>
    );
  };

  // Pour la rétrocompatibilité, on garde le compteur global
  const FreeUsageCounter = () => {
    // Ne pas afficher pour les utilisateurs connectés
    if (user) return null;

    const usagesLeft = MAX_FREE_USAGES - usagesCount;
    const percentageUsed = (usagesCount / MAX_FREE_USAGES) * 100;
    
    return (
      <div className="flex items-center gap-2">
        <div className="w-36 sm:w-48">
          <Progress value={percentageUsed} className="h-2" />
        </div>
        <span className="text-xs text-gray-600">
          {usagesLeft} utilisation{usagesLeft > 1 ? 's' : ''} restante{usagesLeft > 1 ? 's' : ''}
        </span>
      </div>
    );
  };

  return {
    usagesCount,
    toolUsagesCount,
    usagesLeft: MAX_FREE_USAGES - usagesCount,
    toolUsagesLeft: MAX_FREE_TOOL_USAGES - toolUsagesCount,
    hasReachedLimit,
    hasReachedToolLimit,
    incrementUsage,
    resetUsages,
    FreeUsageCounter,
    ToolUsageCounter,
    visitedTools,
    MAX_FREE_TOOL_USAGES
  };
};
